import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ErrorService } from '@interacta-shared/data-access-error';
import {
    ExternalLinksStateService,
    IExternalLinksCategory,
} from '@interacta-shared/feature-external-links';
import { isDefined } from '@interacta-shared/util';
import { i18nStringV2 } from '@interacta-shared/util-common';
import { selecHomeWithUsefulLinks } from '@modules/communities/store/home-pages/home-pages.selectors';
import {
    DigitalWorkplaceDefinition,
    DigitalWorkplaceWidget,
    UsefulLinkWidget,
    WidgetType,
} from '@modules/digital-workplace/models/digital-workplace.model';
import { isUsefulLinkWidget } from '@modules/digital-workplace/models/digital-workplace.utils';
import { LinksDialogStateNormalizeService } from '@modules/state/services/links-dialog-state-normalize.service';
import {
    UsefulLink,
    UsefulLinksGroup,
} from '@modules/useful-link/models/useful-link.model';
import { Store } from '@ngrx/store';
import { combineLatest, EMPTY, Subject, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LinksDialogState } from '../models/links-dialog-state.model';
import { IStateService } from './istate-service';
import { StateService } from './state.service';

@Injectable({ providedIn: 'root' })
export class LinkDialogStateService implements IStateService<LinksDialogState> {
    private readonly store = inject(Store);
    private readonly stateService = inject(StateService);
    private readonly externalLinksStateService = inject(
        ExternalLinksStateService,
    );
    private readonly normalizeService = inject(
        LinksDialogStateNormalizeService,
    );
    private readonly errorService = inject(ErrorService);

    readonly state = this.stateService.linksDialogState;
    readonly extLinkCategories$ = toObservable(
        this.externalLinksStateService.externalLinksCategories,
    );
    flush$ = new Subject<void>();

    open(): void {
        this.state.isOpen.set(true);
        this.getLinks();
    }

    close(): void {
        this.state.isOpen.set(false);
    }

    initialize(): void {
        this.state.externalLinksCategories.set(null);
        this.state.dwDefinitions.set(undefined);
        this.state.normalizedSections.set(undefined);
        this.state.isOpen.set(false);
    }

    flush(): void {
        this.initialize();
        this.flush$.next();
    }

    getLinks(search?: string): void {
        combineLatest([
            this.extLinkCategories$,
            this.store.select(selecHomeWithUsefulLinks),
        ])
            .pipe(
                takeUntil(this.flush$),
                catchError((err) => {
                    this.errorService.handle(err);
                    return EMPTY;
                }),
            )
            .subscribe(([extLinksCategories, dwDefinitions]) => {
                if (search?.trim()) {
                    const tokens = this.tokenizeSearch(search);

                    // Filter external links with hierarchical search
                    const filteredExtLinksCategories =
                        this.filterExternalLinksCategories(
                            extLinksCategories,
                            tokens,
                        );

                    // Filter DW definitions with enhanced search
                    const filteredDwDefinitions = dwDefinitions
                        ?.map((dwDef) =>
                            this.filterDwDefinitionByTokens(dwDef, tokens),
                        )
                        .filter(isDefined);

                    this.state.externalLinksCategories.set(
                        filteredExtLinksCategories,
                    );
                    this.state.dwDefinitions.set(filteredDwDefinitions);
                } else {
                    this.state.externalLinksCategories.set(extLinksCategories);
                    this.state.dwDefinitions.set(dwDefinitions);

                    const counter = this.calculateTotalLinksCount(
                        extLinksCategories,
                        dwDefinitions,
                    );
                    this.state.linksTotalCount.set(counter);
                }

                this.normalizeService.updateNormalizedSections();
            });
    }

    private tokenizeSearch(search: string): string[] {
        return search
            .toLowerCase()
            .trim()
            .split(/\s+/)
            .filter((token) => token.length > 0);
    }

    private matchesAllTokens(text: i18nStringV2, tokens: string[]): boolean {
        const searchText = (text.label ?? '').toLowerCase();
        return tokens.every((token) => searchText.includes(token));
    }

    private matchesAllTokensInString(text: string, tokens: string[]): boolean {
        const searchText = text.toLowerCase();
        return tokens.every((token) => searchText.includes(token));
    }

    /**
     * Filter external links categories based on tokens with hierarchical search.
     * Searches in category label and individual link labels
     */
    private filterExternalLinksCategories(
        categories: IExternalLinksCategory[] | null | undefined,
        tokens: string[],
    ): IExternalLinksCategory[] | null {
        if (!categories) return null;

        const filteredCategories = categories
            .map((category) => {
                if (this.matchesAllTokensInString(category.label, tokens)) {
                    return category;
                }

                const matchingLinks = category.links.filter((link) =>
                    this.matchesAllTokensInString(link.label, tokens),
                );
                return matchingLinks.length > 0
                    ? { ...category, links: matchingLinks }
                    : null;
            })
            .filter(isDefined);

        return filteredCategories.length > 0 ? filteredCategories : null;
    }

    /**
     * Filter DW definition based on tokens with hierarchical search
     */
    private filterDwDefinitionByTokens(
        dwDef: DigitalWorkplaceDefinition,
        tokens: string[],
    ): DigitalWorkplaceDefinition | null {
        if (this.matchesAllTokens(dwDef.name, tokens)) {
            return dwDef;
        }

        const matchingWidgets = dwDef.widgets
            .filter(isUsefulLinkWidget)
            .map((widget: UsefulLinkWidget) => {
                if (this.matchesAllTokens(widget.name, tokens)) {
                    return widget;
                }

                const matchingGroups = widget.linksGroups
                    .map((group: UsefulLinksGroup) => {
                        if (this.matchesAllTokens(group.name, tokens)) {
                            return group;
                        }
                        const matchingLinks = group.links.filter(
                            (link: UsefulLink) =>
                                this.matchesAllTokens(link.name, tokens),
                        );
                        return matchingLinks.length > 0 ? group : null;
                    })
                    .filter(isDefined);

                return matchingGroups.length > 0
                    ? { ...widget, linksGroups: matchingGroups }
                    : null;
            })
            .filter(isDefined);

        return matchingWidgets.length > 0
            ? { ...dwDef, widgets: matchingWidgets }
            : null;
    }

    private calculateTotalLinksCount(
        externalLinksCategories: IExternalLinksCategory[] | null | undefined,
        dwDefinitions: DigitalWorkplaceDefinition[] | undefined,
    ): number {
        const externalCount =
            externalLinksCategories?.reduce(
                (total, category) => total + category.links.length,
                0,
            ) ?? 0;
        const dwCount =
            dwDefinitions?.reduce(
                (acc, curr) =>
                    acc +
                    curr.widgets.reduce(
                        (acc: number, curr: DigitalWorkplaceWidget) => {
                            if (curr.tag === WidgetType.USEFUL_LINK) {
                                return (
                                    acc +
                                    curr.linksGroups.reduce(
                                        (acc: number, curr: UsefulLinksGroup) =>
                                            acc + curr.links.length,
                                        0,
                                    )
                                );
                            }
                            return acc;
                        },
                        0,
                    ),
                0,
            ) ?? 0;
        return externalCount + dwCount;
    }
}
